import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SoftwareApplicationSchema, BreadcrumbSchema, OrganizationSchema } from '@/components/SEOSchema';
import { 
  Check, X, ChevronDown, Shield, Zap, Euro, FileText,
  Clock, ArrowRight, AlertTriangle, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Footer from '@/components/Footer';
import { useTracking, useScrollTracking, useSectionTracking } from '@/hooks/useTracking';
import { useSEO } from '@/hooks/useSEO';

export default function RechnungsvorlageFreelancer() {
  const [openFaq, setOpenFaq] = useState(null);
  const { trackCTAClick, trackFAQOpen } = useTracking();
  useScrollTracking();

  const heroRef = useRef(null);
  const comparisonRef = useRef(null);
  const generatorRef = useRef(null);
  const faqRef = useRef(null);

  useSectionTracking('hero', heroRef);
  useSectionTracking('comparison', comparisonRef);
  useSectionTracking('generator', generatorRef);
  useSectionTracking('faq', faqRef);

  useSEO({
    title: 'Rechnungsvorlage Freelancer – Kostenlos erstellen | ivoliq',
    description: 'Vergiss Word- und Excel-Vorlagen. Erstelle rechtssichere Rechnungen für DE/AT/CH in Sekunden – kostenlos, ohne Anmeldung, als PDF. Alle Pflichtangaben automatisch.',
    canonical: 'https://ivoliq.io/rechnungsvorlage-freelancer',
  });

  const comparisonRows = [
    { feature: 'Pflichtangaben automatisch', template: false, generator: true },
    { feature: 'Steuersatz-Berechnung (MwSt/USt)', template: false, generator: true },
    { feature: 'Kleinunternehmer-Hinweis', template: false, generator: true },
    { feature: 'PDF-Download sofort', template: false, generator: true },
    { feature: 'Keine Software nötig', template: false, generator: true },
    { feature: 'Fortlaufende Nummerierung', template: false, generator: true },
    { feature: 'DSGVO-konform (keine Serverübertragung)', template: false, generator: true },
    { feature: 'Swiss QR-Bill (Schweiz)', template: false, generator: true },
    { feature: 'Rechtssicher nach § 14 UStG / § 11 UStG / MWStG', template: false, generator: true },
  ];

  const problems = [
    {
      icon: AlertTriangle,
      title: 'Fehlende Pflichtangaben',
      desc: 'Word/Excel-Vorlagen enthalten oft nicht alle gesetzlichen Pflichtangaben. Ein Fehler kann zu Rechnungsrückweisungen und Steuerproblemen führen.',
    },
    {
      icon: Euro,
      title: 'Manuelle Berechnung',
      desc: 'MwSt, Netto, Brutto – bei Vorlagen musst du alles selbst rechnen. Rundungsfehler und falsche Steuersätze sind vorprogrammiert.',
    },
    {
      icon: Clock,
      title: 'Zeitverschwendung',
      desc: 'Vorlage öffnen, Daten eintragen, als PDF exportieren, Nummerierung prüfen – das kostet jedes Mal 15+ Minuten statt 30 Sekunden.',
    },
  ];

  const countries = [
    {
      flag: '\u{1F1E9}\u{1F1EA}',
      name: 'Deutschland',
      law: '§ 14 UStG',
      features: ['19% / 7% USt', 'Kleinunternehmer § 19', 'Steuernummer oder USt-IdNr.'],
      link: '/rechnung-erstellen-kostenlos-deutschland',
      color: 'border-t-red-500',
    },
    {
      flag: '\u{1F1E6}\u{1F1F9}',
      name: 'Österreich',
      law: '§ 11 UStG',
      features: ['20% / 13% / 10% USt', 'Kleinunternehmer § 6 Z 27', 'UID-Nummer'],
      link: '/rechnung-erstellen-kostenlos-oesterreich',
      color: 'border-t-orange-500',
    },
    {
      flag: '\u{1F1E8}\u{1F1ED}',
      name: 'Schweiz',
      law: 'MWStG',
      features: ['8.1% / 2.6% MWST', 'Swiss QR-Bill integriert', 'MWST-Nr. / CHE-Nr.'],
      link: '/qr-rechnung-erstellen-kostenlos',
      color: 'border-t-red-600',
    },
  ];

  const faqs = [
    {
      q: 'Brauche ich noch eine Word/Excel-Rechnungsvorlage?',
      a: 'Nein. Der ivoliq Rechnungsgenerator ersetzt jede Vorlage vollständig. Er enthält alle gesetzlichen Pflichtangaben automatisch, berechnet Steuern korrekt und erstellt sofort ein PDF – kostenlos und ohne Anmeldung.',
    },
    {
      q: 'Ist der Rechnungsgenerator wirklich kostenlos?',
      a: 'Ja, 100% kostenlos und ohne Anmeldung. Du kannst unbegrenzt Rechnungen erstellen und als PDF herunterladen. Deine Daten bleiben in deinem Browser – nichts wird an unsere Server übertragen.',
    },
    {
      q: 'Welche Pflichtangaben enthält die generierte Rechnung?',
      a: 'Alle gesetzlich vorgeschriebenen Angaben: Name und Anschrift beider Parteien, Steuernummer/USt-IdNr., Rechnungsnummer, Rechnungsdatum, Leistungszeitraum, Positionsdetails, Nettobetrag, Steuersatz und -betrag, Bruttobetrag sowie Bankverbindung. Bei Kleinunternehmern wird automatisch der korrekte Hinweistext eingefügt.',
    },
    {
      q: 'Funktioniert der Generator für Deutschland, Österreich und die Schweiz?',
      a: 'Ja! Es gibt jeweils einen eigenen Generator mit länderspezifischen Steuersätzen, Pflichtangaben und Rechtstexten. Der Schweizer Generator unterstützt zusätzlich Swiss QR-Bill.',
    },
    {
      q: 'Werden meine Rechnungsdaten gespeichert?',
      a: 'Nein. Alle Daten werden ausschließlich in deinem Browser verarbeitet und nie an Server übertragen. Das macht den Generator 100% DSGVO-konform. Optional kannst du Absenderdaten lokal in deinem Browser speichern, um sie beim nächsten Mal wiederzuverwenden.',
    },
    {
      q: 'Was ist der Unterschied zum ivoliq Pro-Plan?',
      a: 'Der Generator ist ideal für einzelne Rechnungen. Mit einem ivoliq Konto (ab 0€) kannst du zusätzlich: Kunden und Rechnungen speichern, automatische Nummernkreise nutzen, Rechnungen per E-Mail versenden, Zeiterfassung nutzen und deine Finanzen überblicken.',
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": { "@type": "Answer", "text": faq.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <SoftwareApplicationSchema country="DE" />
      <BreadcrumbSchema items={[
        { name: "Home", url: "/" },
        { name: "Rechnungsvorlage Freelancer" },
      ]} />
      <OrganizationSchema />

      {/* Hero */}
      <section ref={heroRef} className="bg-gradient-to-b from-emerald-50 to-white pt-20 pb-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 text-sm font-medium px-4 py-1.5 rounded-full mb-6">
            <AlertTriangle className="h-4 w-4" />
            Word/Excel-Vorlagen sind fehleranfällig
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Rechnungsvorlage?{' '}
            <span className="text-emerald-600">Geht einfacher.</span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Erstelle rechtssichere Rechnungen für Deutschland, Österreich und die Schweiz in 30 Sekunden – kostenlos, ohne Anmeldung, als PDF.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
            <Link to="/rechnung-erstellen-kostenlos-deutschland" onClick={() => trackCTAClick('hero', 'Rechnung erstellen DE')}>
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg w-full sm:w-auto" data-testid="hero-cta-de">
                <Zap className="h-5 w-5 mr-2" />
                Jetzt Rechnung erstellen
              </Button>
            </Link>
          </div>

          <p className="text-sm text-gray-500">
            Kostenlos &middot; Ohne Anmeldung &middot; Alle Pflichtangaben automatisch
          </p>
        </div>
      </section>

      {/* Problems with templates */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-12">
            Warum Word/Excel-Vorlagen ein Problem sind
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {problems.map((p, i) => (
              <Card key={i} className="border-none shadow-sm">
                <CardContent className="p-6">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                    <p.icon className="h-5 w-5 text-amber-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{p.title}</h3>
                  <p className="text-sm text-gray-600">{p.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section ref={comparisonRef} className="py-16">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-12">
            Vorlage vs. Online-Generator
          </h2>
          <div className="border rounded-xl overflow-hidden">
            <div className="grid grid-cols-3 bg-gray-50 border-b">
              <div className="p-4 font-medium text-gray-700 text-sm">Funktion</div>
              <div className="p-4 text-center font-medium text-gray-500 text-sm">Word/Excel</div>
              <div className="p-4 text-center font-medium text-emerald-700 text-sm bg-emerald-50">ivoliq Generator</div>
            </div>
            {comparisonRows.map((row, i) => (
              <div key={i} className={`grid grid-cols-3 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} border-b last:border-0`}>
                <div className="p-3 text-sm text-gray-700">{row.feature}</div>
                <div className="p-3 flex justify-center">
                  <X className="h-5 w-5 text-red-400" />
                </div>
                <div className="p-3 flex justify-center bg-emerald-50/30">
                  <Check className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Country-specific generators */}
      <section ref={generatorRef} className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-4">
            Rechnung erstellen – für dein Land
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Jeder Generator ist auf die rechtlichen Anforderungen des jeweiligen Landes zugeschnitten.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {countries.map((c) => (
              <Card key={c.name} className={`hover:shadow-lg transition-shadow border-t-4 ${c.color}`}>
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <div className="text-3xl mb-3">{c.flag}</div>
                    <h3 className="text-xl font-bold text-gray-900">Rechnung {c.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">Nach {c.law}</p>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-600 mb-6">
                    {c.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>PDF-Download sofort</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>Ohne Anmeldung</span>
                    </li>
                  </ul>
                  <Link to={c.link} onClick={() => trackCTAClick('generator', `Rechnung erstellen ${c.name}`)}>
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                      <Download className="h-4 w-4 mr-2" />
                      Rechnung erstellen
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / How it works */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-12">
            So funktioniert&apos;s
          </h2>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              { step: '1', title: 'Daten eingeben', desc: 'Absender, Empfänger und Positionen ausfüllen. Pflichtangaben sind markiert.' },
              { step: '2', title: 'Steuern werden berechnet', desc: 'MwSt/USt wird automatisch berechnet. Kleinunternehmer-Option verfügbar.' },
              { step: '3', title: 'PDF herunterladen', desc: 'Rechnung als professionelles PDF herunterladen – kostenlos, sofort, ohne Anmeldung.' },
            ].map((s, i) => (
              <div key={i}>
                <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {s.step}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-emerald-50 border border-emerald-200 rounded-xl p-6 flex flex-col md:flex-row items-center gap-4">
            <Shield className="h-8 w-8 text-emerald-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-gray-900">100% DSGVO-konform</p>
              <p className="text-sm text-gray-600">Deine Daten werden nur in deinem Browser verarbeitet – nichts wird an Server übertragen.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section ref={faqRef} className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-12">
            Häufige Fragen
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <button
                  style={{ display: "flex" }}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    setOpenFaq(openFaq === i ? null : i);
                    if (openFaq !== i) trackFAQOpen(faq.q);
                  }}
                >
                  <span className="font-medium text-gray-900 pr-4">{faq.q}</span>
                  <ChevronDown className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-b from-white to-emerald-50">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-4">
            Bereit, deine erste Rechnung zu erstellen?
          </h2>
          <p className="text-gray-600 mb-8">
            Kostenlos, ohne Anmeldung, in 30 Sekunden.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/rechnung-erstellen-kostenlos-deutschland" onClick={() => trackCTAClick('final_cta', 'DE')}>
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg w-full sm:w-auto" data-testid="final-cta-button">
                Rechnung erstellen (DE)
              </Button>
            </Link>
            <Link to="/rechnung-erstellen-kostenlos-oesterreich" onClick={() => trackCTAClick('final_cta', 'AT')}>
              <Button size="lg" variant="outline" className="px-6 py-6 text-lg w-full sm:w-auto">
                Österreich
              </Button>
            </Link>
            <Link to="/qr-rechnung-erstellen-kostenlos" onClick={() => trackCTAClick('final_cta', 'CH')}>
              <Button size="lg" variant="outline" className="px-6 py-6 text-lg w-full sm:w-auto">
                Schweiz
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Related pages */}
      <section className="py-12 border-t">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-sm text-gray-500 text-center mb-6">Weitere Seiten</p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link to="/buchhaltung-freelancer-oesterreich" className="text-emerald-600 hover:underline">Buchhaltung für Freelancer in Österreich</Link>
            <Link to="/belegerfassung-freelancer" className="text-emerald-600 hover:underline">Belegerfassung</Link>
            <Link to="/steuerruecklagen-freelancer" className="text-emerald-600 hover:underline">Steuerrücklagen</Link>
            <Link to="/lexoffice-alternative" className="text-emerald-600 hover:underline">lexoffice Alternative</Link>
            <Link to="/sevdesk-alternative" className="text-emerald-600 hover:underline">sevDesk Alternative</Link>
            <Link to="/pricing" className="text-emerald-600 hover:underline">Preise</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
